import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Link } from "react-router-dom";
import {
  AlertTriangle, ArrowDownToLine, Camera, CheckCircle2, Clock, Database, DownloadCloud, HardDrive, Link2, Loader2,
  Play, RefreshCw, RotateCcw, Server, ShieldCheck, Trash2, XCircle,
} from "lucide-react";
import { toast } from "sonner";
import { api, errMsg, fmtBytes, fmtNum } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Progress } from "@/components/ui/progress";
import { ConfirmDialog } from "@/components/ConfirmDialog";
import { Code, Metric, MetricGrid, PageHeader } from "@/components/shared";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription,
  AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";

const STALE_SECS = 36 * 3600; // sem backup há mais de 36 h = atenção

export function fmtSnapId(id) {
  const m = /^(\d{4})-(\d{2})-(\d{2})T(\d{2})-(\d{2})-(\d{2})Z(?:_(\d+))?$/.exec(id || "");
  return m ? `${m[3]}/${m[2]}/${m[1]} ${m[4]}:${m[5]}:${m[6]} UTC${m[7] ? ` #${m[7]}` : ""}` : id;
}

export function fmtAge(s) {
  if (s == null) return "nunca";
  if (s < 60) return "agora mesmo";
  if (s < 3600) return `há ${Math.floor(s / 60)} min`;
  if (s < 86400) return `há ${Math.floor(s / 3600)} h`;
  return `há ${Math.floor(s / 86400)} dia(s)`;
}

const fmtIn = (s) => {
  if (s == null) return "";
  if (s < 3600) return `em ${Math.max(1, Math.floor(s / 60))} min`;
  return `em ${Math.floor(s / 3600)} h ${Math.floor((s % 3600) / 60)} min`;
};

const fmtDur = (ms) => {
  const s = Math.floor(ms / 1000);
  if (s < 60) return `${s}s`;
  if (s < 3600) return `${Math.floor(s / 60)}m ${s % 60}s`;
  return `${Math.floor(s / 3600)}h ${Math.floor((s % 3600) / 60)}m`;
};

function dbStatus(d) {
  if (!d.last) return { level: "never", label: "Sem backup", cls: "bg-red-500/15 text-red-300 border-red-500/30" };
  if (d.age_secs > STALE_SECS) return { level: "stale", label: `Atrasado (${fmtAge(d.age_secs)})`, cls: "bg-amber-500/15 text-amber-300 border-amber-500/30" };
  return { level: "ok", label: "Protegido", cls: "bg-emerald-500/15 text-emerald-300 border-emerald-500/30" };
}

const STATUS_PT = { login: "Autenticando na origem…", downloading: "Baixando…", restoring: "Restaurando banco…", done: "Concluído", error: "Falhou", cancelled: "Cancelado" };

function JobCard({ job, onCancel }) {
  const pct = job.bytes_total ? Math.min(100, (job.bytes_done / job.bytes_total) * 100) : job.done ? 100 : 0;
  const eta = job.bytes_total && job.bytes_per_sec ? (job.bytes_total - job.bytes_done) / job.bytes_per_sec : null;
  const Icon = job.status === "done" ? CheckCircle2 : job.status === "error" || job.status === "cancelled" ? XCircle : Loader2;
  const color = job.status === "done" ? "text-emerald-400" : job.status === "error" ? "text-red-400" : job.status === "cancelled" ? "text-zinc-400" : "text-orange-400";
  return (
    <div className="rounded-sm border border-zinc-800 bg-zinc-950/60 p-4 space-y-3" data-testid={`pull-job-${job.id}`}>
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <div className="flex items-center gap-2 text-sm text-white">
            <Icon className={`h-4 w-4 ${color} ${!job.done ? "animate-spin" : ""}`} />
            <span className="font-mono truncate">{job.db} · {fmtSnapId(job.snapshot_id)}</span>
            {job.restore && job.target !== job.db && <span className="text-zinc-500">→ {job.target}</span>}
          </div>
          <div className="text-xs text-zinc-500 mt-1 truncate">de {job.source} · {job.restore ? "puxar e restaurar" : "só copiar snapshot"}{job.mode === "parallel" ? ` · ${job.parallel} conexões paralelas · retomada + CRC32` : job.mode === "tar" ? " · origem antiga (1 conexão)" : ""}</div>
          {job.note && <div className="text-xs text-zinc-400 mt-0.5 truncate" data-testid={`pull-note-${job.id}`}>{job.note}</div>}
        </div>
        {!job.done && <Button size="sm" variant="outline" onClick={() => onCancel(job.id)} data-testid={`pull-cancel-${job.id}`} className="rounded-sm border-zinc-700 text-red-400">Cancelar</Button>}
      </div>
      <Progress value={pct} className="h-2 bg-zinc-800" data-testid={`pull-progress-${job.id}`} />
      <div className="flex flex-wrap justify-between gap-2 text-xs text-zinc-400">
        <span data-testid={`pull-status-${job.id}`} className={color}>{STATUS_PT[job.status] || job.status}{job.error ? `: ${job.error}` : ""}</span>
        <span className="font-mono">
          {fmtBytes(job.bytes_done)}{job.bytes_total ? ` / ${fmtBytes(job.bytes_total)}` : ""} · {job.bytes_total ? `${pct.toFixed(0)}%` : "…"} · {fmtBytes(job.bytes_per_sec)}/s · {fmtDur(job.elapsed_ms)}
          {eta != null && !job.done && job.status === "downloading" ? ` · resta ~${fmtDur(eta * 1000)}` : ""} · {fmtNum(job.files_done)}{job.files_total ? `/${fmtNum(job.files_total)}` : ""} arquivo(s)
        </span>
      </div>
    </div>
  );
}

export default function Backup() {
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(false);
  const [form, setForm] = useState(null); // agendamento
  const [remote, setRemote] = useState({ source_url: "", email: "", password: "" });
  const [browse, setBrowse] = useState(null);
  const [browsing, setBrowsing] = useState(false);
  const [pick, setPick] = useState({ db: "", snapshot_id: "", as: "", restore: true });
  const [pulling, setPulling] = useState(false);
  const [orphanRestore, setOrphanRestore] = useState(null); // {db,id}
  const [orphanAs, setOrphanAs] = useState("");
  const [orphanDel, setOrphanDel] = useState(null);
  const timer = useRef(null);

  const load = useCallback(async (silent) => {
    try {
      const { data } = await api.get("/backup/overview");
      setData(data);
      setForm((f) => f ?? { enabled: data.settings.enabled, time: data.settings.time, keep: data.settings.keep });
    } catch (e) { if (!silent) toast.error(errMsg(e)); }
  }, []);

  const hasActiveJob = !!data?.jobs?.some((j) => !j.done);
  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    clearInterval(timer.current);
    timer.current = setInterval(() => load(true), hasActiveJob ? 1500 : 15000);
    return () => clearInterval(timer.current);
  }, [hasActiveJob, load]);

  const saveSettings = async () => {
    setBusy(true);
    try {
      const { data: s } = await api.put("/backup/settings", { enabled: form.enabled, time: form.time, keep: Number(form.keep) });
      toast.success(s.enabled ? `Backup automático ligado: todo dia às ${s.time} UTC, mantendo ${s.keep}` : "Backup automático desligado");
      setForm({ enabled: s.enabled, time: s.time, keep: s.keep }); load(true);
    } catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };

  const runAll = async () => {
    setBusy(true);
    try { const { data: r } = await api.post("/backup/run"); toast.success(`Backup de todos os bancos: ${r.result.summary}`); load(true); }
    catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };

  const snapOne = async (name) => {
    try { await api.post(`/databases/${name}/snapshots`, { label: "manual (página Backup)" }); toast.success(`Snapshot de "${name}" criado`); load(true); }
    catch (e) { toast.error(errMsg(e)); }
  };

  const connect = async () => {
    setBrowsing(true); setBrowse(null);
    try {
      const { data: b } = await api.post("/backup/remote/browse", remote);
      setBrowse(b);
      const first = b.databases.find((d) => d.snapshots?.length) || b.databases[0];
      setPick({ db: first?.name || "", snapshot_id: first?.snapshots?.[0]?.id || "", as: "", restore: true });
      toast.success(`Conectado a ${b.source} (AxonDB v${b.version}) — ${b.databases.length} banco(s)`);
    } catch (e) { toast.error(errMsg(e)); } finally { setBrowsing(false); }
  };

  const startPull = async () => {
    if (!pick.db || !pick.snapshot_id) return toast.error("Escolha o banco e o snapshot de origem");
    setPulling(true);
    try {
      await api.post("/backup/pull", { ...remote, db: pick.db, snapshot_id: pick.snapshot_id, as: pick.as || undefined, restore: pick.restore });
      toast.success("Transferência iniciada — acompanhe a barra de progresso"); load(true);
    } catch (e) { toast.error(errMsg(e)); } finally { setPulling(false); }
  };

  const cancelJob = async (id) => {
    try { await api.delete(`/backup/jobs/${id}`); toast("Cancelando…"); } catch (e) { toast.error(errMsg(e)); }
  };

  const doOrphanRestore = async () => {
    const { db, id } = orphanRestore; const target = orphanAs.trim(); setOrphanRestore(null); setBusy(true);
    try {
      const { data: r } = await api.post(`/backup/snapshots/${db}/${id}/restore`, target ? { as: target } : {});
      toast.success(`Banco "${r.target}" restaurado a partir do snapshot recebido`); setOrphanAs(""); load(true);
    } catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };

  const doOrphanDelete = async () => {
    const { db, id } = orphanDel; setOrphanDel(null);
    try { await api.delete(`/databases/${db}/snapshots/${id}`); toast.success("Snapshot apagado"); load(true); } catch (e) { toast.error(errMsg(e)); }
  };

  const s = data?.settings;
  const dbs = useMemo(() => data?.databases || [], [data]);
  const problems = useMemo(() => dbs.filter((d) => dbStatus(d).level !== "ok"), [dbs]);
  const remoteDb = browse?.databases.find((d) => d.name === pick.db);
  const origin = typeof window !== "undefined" ? window.location.origin : "";

  return (
    <div data-testid="backup-page">
      <PageHeader overline="Proteção de dados" title="Backup">
        <Button variant="outline" onClick={() => load()} data-testid="backup-refresh-btn" className="rounded-sm border-zinc-700"><RefreshCw className="h-4 w-4" /></Button>
        <Button onClick={runAll} disabled={busy || !dbs.length} data-testid="backup-run-all-btn" className="rounded-sm bg-orange-600 hover:bg-orange-500 text-white btn-tactile">
          <Camera className="h-4 w-4 mr-1" /> Fazer backup de todos agora
        </Button>
      </PageHeader>

      <MetricGrid>
        <Metric label="Agendamento" value={s ? (s.enabled ? `${s.time} UTC` : "Desligado") : "…"} accent={s?.enabled} hint={s?.enabled ? `próximo ${fmtIn(s.next_run_in_secs)} · mantém ${s.keep}` : "ligue abaixo para proteger todo dia"} testId="metric-schedule" />
        <Metric label="Snapshots" value={data ? fmtNum(data.totals.snapshots) : "…"} hint={data ? `retendo ${fmtBytes(data.totals.bytes_exclusive)} extra em disco` : ""} testId="metric-snapshots" />
        <Metric label="Bancos protegidos" value={data ? `${dbs.length - problems.length}/${dbs.length}` : "…"} accent={problems.length > 0} hint={problems.length ? `${problems.length} precisa(m) de atenção` : "todos com backup recente"} testId="metric-protected" />
        <Metric label="Disco livre" value={data ? fmtBytes(data.disk_free_bytes) : "…"} hint={data?.data_dir} testId="metric-disk" />
      </MetricGrid>

      {problems.length > 0 && (
        <div className="mt-6 rounded-sm border border-amber-500/30 bg-amber-500/10 p-4 flex gap-3 text-sm" data-testid="backup-alert">
          <AlertTriangle className="h-5 w-5 text-amber-400 shrink-0" />
          <div>
            <div className="text-amber-200 font-medium">{problems.length} banco(s) sem backup recente: {problems.map((d) => d.name).join(", ")}</div>
            <div className="text-amber-200/70 mt-0.5">Clique em “Fazer backup de todos agora” ou ligue o agendamento. Um snapshot leva milissegundos e custa ~0 bytes.</div>
          </div>
        </div>
      )}

      <div className="grid lg:grid-cols-2 gap-6 mt-8">
        <div className="rounded-sm border border-zinc-800 bg-zinc-900/40 p-5 space-y-4" data-testid="schedule-card">
          <div className="flex items-center gap-2 text-white font-display"><Clock className="h-4 w-4 text-orange-500" /> Backup automático diário</div>
          <p className="text-sm text-zinc-400">Todo dia no horário escolhido (UTC — Brasília = UTC−3) o servidor cria um snapshot de <b>cada banco</b> e apaga os mais antigos além do limite. Não precisa mexer no <code className="font-mono text-zinc-200">.env</code>.</p>
          {form && (
            <div className="grid sm:grid-cols-[auto_1fr_1fr_auto] gap-3 items-end">
              <div className="flex items-center gap-2 pb-2">
                <Switch checked={form.enabled} onCheckedChange={(v) => setForm({ ...form, enabled: v })} data-testid="schedule-enabled-switch" />
                <span className="text-sm text-zinc-300">{form.enabled ? "Ligado" : "Desligado"}</span>
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="sched-time" className="text-zinc-300">Horário (UTC)</Label>
                <Input id="sched-time" type="time" value={form.time} onChange={(e) => setForm({ ...form, time: e.target.value })} data-testid="schedule-time-input" className="bg-zinc-950 border-zinc-800 rounded-sm font-mono" />
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="sched-keep" className="text-zinc-300">Manter (por banco)</Label>
                <Input id="sched-keep" type="number" min={1} max={365} value={form.keep} onChange={(e) => setForm({ ...form, keep: e.target.value })} data-testid="schedule-keep-input" className="bg-zinc-950 border-zinc-800 rounded-sm font-mono" />
              </div>
              <Button onClick={saveSettings} disabled={busy} data-testid="schedule-save-btn" className="rounded-sm bg-orange-600 hover:bg-orange-500 text-white btn-tactile">Salvar</Button>
            </div>
          )}
          <div className="text-xs text-zinc-500 border-t border-zinc-800 pt-3" data-testid="schedule-last-run">
            {s?.last_run ? <>Última execução: <span className="font-mono text-zinc-300">{s.last_run}</span> — {s.last_result}</> : "Ainda não rodou neste servidor."}
            {s?.enabled && s.next_run && <> · Próxima: <span className="font-mono text-zinc-300">{s.next_run}</span></>}
          </div>
        </div>

        <div className="rounded-sm border border-zinc-800 bg-zinc-900/40 p-5 space-y-3" data-testid="howto-card">
          <div className="flex items-center gap-2 text-white font-display"><ShieldCheck className="h-4 w-4 text-orange-500" /> Como o backup funciona</div>
          <ul className="text-sm text-zinc-400 space-y-2 list-disc pl-5">
            <li><b className="text-zinc-200">Snapshot</b> = foto do banco naquele instante. Como as SSTables são imutáveis, ele usa hard links: leva milissegundos e ocupa ~0 bytes até a compactação trocar arquivos.</li>
            <li><b className="text-zinc-200">Restaurar</b> volta o banco ao ponto do snapshot (um snapshot “pre-restore” do estado atual é criado antes, nada se perde) ou cria um banco novo com outro nome.</li>
            <li><b className="text-zinc-200">Snapshot não protege contra a perda da máquina</b> — ele fica no mesmo disco. Para isso, leve-o para <b>outro servidor</b> com a seção abaixo.</li>
            <li>O que foi gravado <i>depois</i> do snapshot não está nele. Antes de migrar, crie um snapshot novo.</li>
          </ul>
        </div>
      </div>

      <div className="mt-8 rounded-sm border border-zinc-800 overflow-hidden" data-testid="backup-db-list">
        <div className="flex items-center justify-between px-4 py-3 border-b border-zinc-800 text-sm">
          <span className="text-zinc-300 flex items-center gap-2"><Database className="h-4 w-4" /> Bancos deste servidor</span>
          <span className="text-zinc-500">{dbs.length} banco(s)</span>
        </div>
        {data && dbs.length === 0 && <div className="px-4 py-8 text-center text-zinc-500 text-sm" data-testid="backup-db-empty">Nenhum banco ainda.</div>}
        {dbs.map((d) => {
          const stt = dbStatus(d);
          return (
            <div key={d.name} data-testid={`backup-db-${d.name}`} className="flex flex-col md:flex-row md:items-center gap-3 px-4 py-3 border-b border-zinc-800/60 last:border-0 hover:bg-zinc-900/40 transition-colors">
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2">
                  <Link to={`/databases/${d.name}`} className="font-mono text-sm text-white hover:text-orange-400">{d.name}</Link>
                  <span className={`text-[11px] px-2 py-0.5 rounded-sm border ${stt.cls}`} data-testid={`backup-db-status-${d.name}`}>{stt.label}</span>
                </div>
                <div className="text-xs text-zinc-500 mt-0.5">
                  {fmtNum(d.keys_approx)} chaves · {fmtBytes(d.disk_bytes)} · {d.snapshots} snapshot(s) · último: {d.last ? `${fmtSnapId(d.last.id)} (${fmtAge(d.age_secs)})${d.last.label ? ` — ${d.last.label}` : ""}` : "nunca"}
                </div>
              </div>
              <div className="flex gap-2">
                <Button size="sm" variant="outline" onClick={() => snapOne(d.name)} data-testid={`backup-db-snap-${d.name}`} className="rounded-sm border-zinc-700"><Camera className="h-4 w-4 mr-1" /> Snapshot agora</Button>
                <Button size="sm" variant="ghost" asChild className="rounded-sm text-zinc-300"><Link to={`/databases/${d.name}`} data-testid={`backup-db-open-${d.name}`}>Ver snapshots</Link></Button>
              </div>
            </div>
          );
        })}
      </div>

      <div className="mt-8 rounded-sm border border-zinc-800 bg-zinc-900/40 p-5 space-y-5" data-testid="pull-card">
        <div>
          <div className="flex items-center gap-2 text-white font-display"><DownloadCloud className="h-4 w-4 text-orange-500" /> Trazer um banco de outro servidor</div>
          <p className="text-sm text-zinc-400 mt-1">Informe a URL e o login admin do servidor de <b>origem</b>. Este servidor conecta, lista os snapshots de lá e <b>copia direto servidor→servidor</b> (não passa pelo seu computador), depois restaura o banco aqui. Funciona para bancos de qualquer tamanho, com progresso em tempo real.</p>
        </div>
        <div className="grid sm:grid-cols-[2fr_1.5fr_1.5fr_auto] gap-3 items-end">
          <div className="space-y-1.5">
            <Label htmlFor="src-url" className="text-zinc-300">URL do servidor de origem</Label>
            <Input id="src-url" value={remote.source_url} onChange={(e) => setRemote({ ...remote, source_url: e.target.value })} placeholder="https://axondb.exemplo.com" data-testid="pull-source-url" className="bg-zinc-950 border-zinc-800 rounded-sm font-mono" />
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="src-email" className="text-zinc-300">E-mail admin (origem)</Label>
            <Input id="src-email" value={remote.email} onChange={(e) => setRemote({ ...remote, email: e.target.value })} placeholder="admin@..." data-testid="pull-source-email" className="bg-zinc-950 border-zinc-800 rounded-sm" />
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="src-pass" className="text-zinc-300">Senha (origem)</Label>
            <Input id="src-pass" type="password" value={remote.password} onChange={(e) => setRemote({ ...remote, password: e.target.value })} data-testid="pull-source-password" className="bg-zinc-950 border-zinc-800 rounded-sm" />
          </div>
          <Button onClick={connect} disabled={browsing || !remote.source_url || !remote.email || !remote.password} data-testid="pull-connect-btn" className="rounded-sm bg-zinc-100 text-zinc-900 hover:bg-white btn-tactile">
            {browsing ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : <Link2 className="h-4 w-4 mr-1" />} Conectar
          </Button>
        </div>

        {browse && (
          <div className="rounded-sm border border-zinc-800 bg-zinc-950/60 p-4 space-y-4" data-testid="pull-picker">
            <div className="text-xs text-zinc-500 flex items-center gap-2"><Server className="h-3.5 w-3.5" /> {browse.source} · AxonDB v{browse.version} · {browse.databases.length} banco(s)</div>
            {browse.databases.length === 0 && <div className="text-sm text-zinc-400">O servidor de origem não tem bancos.</div>}
            <div className="grid sm:grid-cols-2 gap-3">
              <div className="space-y-1.5">
                <Label className="text-zinc-300">Banco de origem</Label>
                <select value={pick.db} onChange={(e) => { const d = browse.databases.find((x) => x.name === e.target.value); setPick({ ...pick, db: e.target.value, snapshot_id: d?.snapshots?.[0]?.id || "" }); }} data-testid="pull-db-select" className="w-full h-10 px-3 bg-zinc-950 border border-zinc-800 rounded-sm text-sm font-mono text-white">
                  {browse.databases.map((d) => <option key={d.name} value={d.name}>{d.name} — {fmtNum(d.keys_approx || 0)} chaves, {d.snapshots?.length || 0} snapshot(s)</option>)}
                </select>
              </div>
              <div className="space-y-1.5">
                <Label className="text-zinc-300">Snapshot</Label>
                <select value={pick.snapshot_id} onChange={(e) => setPick({ ...pick, snapshot_id: e.target.value })} data-testid="pull-snapshot-select" className="w-full h-10 px-3 bg-zinc-950 border border-zinc-800 rounded-sm text-sm font-mono text-white">
                  {(remoteDb?.snapshots || []).map((sn) => <option key={sn.id} value={sn.id}>{fmtSnapId(sn.id)}{sn.label ? ` — ${sn.label}` : ""} ({fmtBytes(sn.bytes_total)})</option>)}
                  {!remoteDb?.snapshots?.length && <option value="">— sem snapshots: crie um no servidor de origem —</option>}
                </select>
              </div>
            </div>
            <div className="grid sm:grid-cols-[1fr_auto_auto] gap-3 items-end">
              <div className="space-y-1.5">
                <Label htmlFor="pull-as" className="text-zinc-300">Nome do banco aqui (opcional)</Label>
                <Input id="pull-as" value={pick.as} onChange={(e) => setPick({ ...pick, as: e.target.value })} placeholder={pick.db || "mesmo nome da origem"} data-testid="pull-as-input" className="bg-zinc-950 border-zinc-800 rounded-sm font-mono" />
              </div>
              <label className="flex items-center gap-2 text-sm text-zinc-300 pb-2.5 cursor-pointer">
                <input type="checkbox" checked={pick.restore} onChange={(e) => setPick({ ...pick, restore: e.target.checked })} data-testid="pull-restore-checkbox" className="accent-orange-600" /> Restaurar ao terminar
              </label>
              <Button onClick={startPull} disabled={pulling || hasActiveJob || !pick.snapshot_id} data-testid="pull-start-btn" className="rounded-sm bg-orange-600 hover:bg-orange-500 text-white btn-tactile">
                <ArrowDownToLine className="h-4 w-4 mr-1" /> {pick.restore ? "Puxar e restaurar" : "Só copiar snapshot"}
              </Button>
            </div>
            {pick.restore && pick.db && !pick.as && dbs.some((d) => d.name === pick.db) && (
              <div className="text-xs text-amber-300/90 flex items-center gap-2"><AlertTriangle className="h-3.5 w-3.5" /> O banco “{pick.db}” já existe aqui: será substituído pelo snapshot (um snapshot “pre-restore” do estado atual é criado antes). Informe outro nome para manter os dois.</div>
            )}
          </div>
        )}

        {data?.jobs?.length > 0 && (
          <div className="space-y-3" data-testid="pull-jobs">
            <div className="text-xs overline-label">Transferências</div>
            {data.jobs.slice(0, 5).map((j) => <JobCard key={j.id} job={j} onCancel={cancelJob} />)}
          </div>
        )}
      </div>

      {data?.orphans?.length > 0 && (
        <div className="mt-8 rounded-sm border border-zinc-800 overflow-hidden" data-testid="orphan-list">
          <div className="px-4 py-3 border-b border-zinc-800 text-sm text-zinc-300 flex items-center gap-2"><HardDrive className="h-4 w-4" /> Snapshots recebidos sem banco correspondente</div>
          {data.orphans.map((o) => o.snapshots.map((sn) => (
            <div key={`${o.db}/${sn.id}`} data-testid={`orphan-${o.db}-${sn.id}`} className="flex flex-col md:flex-row md:items-center gap-3 px-4 py-3 border-b border-zinc-800/60 last:border-0">
              <div className="flex-1 min-w-0">
                <div className="font-mono text-sm text-white">{o.db} · {fmtSnapId(sn.id)} {sn.label && <span className="ml-2 text-zinc-400 font-sans">· {sn.label}</span>}</div>
                <div className="text-xs text-zinc-500 mt-0.5">{sn.kv_ssts} SST dados · {sn.idx_ssts} SST índice · {sn.collections} coleções · {fmtBytes(sn.bytes_total)}</div>
              </div>
              <div className="flex gap-2">
                <Button size="sm" onClick={() => setOrphanRestore({ db: o.db, id: sn.id })} data-testid={`orphan-restore-${o.db}-${sn.id}`} className="rounded-sm bg-orange-600 hover:bg-orange-500 text-white"><RotateCcw className="h-4 w-4 mr-1" /> Restaurar como banco</Button>
                <Button size="sm" variant="outline" onClick={() => setOrphanDel({ db: o.db, id: sn.id })} data-testid={`orphan-delete-${o.db}-${sn.id}`} className="rounded-sm border-zinc-700 text-red-400"><Trash2 className="h-4 w-4" /></Button>
              </div>
            </div>
          )))}
        </div>
      )}

      <div className="mt-8 rounded-sm border border-zinc-800 bg-zinc-900/40 p-5 space-y-3" data-testid="migrate-card">
        <div className="flex items-center gap-2 text-white font-display"><Play className="h-4 w-4 text-orange-500" /> Levar um banco DESTE servidor para outro</div>
        <ol className="text-sm text-zinc-400 space-y-1.5 list-decimal pl-5">
          <li>Crie um snapshot fresco aqui (botão “Snapshot agora” acima).</li>
          <li>No <b>servidor de destino</b> (mesma versão do AxonDB), abra <b>Backup → Trazer um banco de outro servidor</b> e informe a URL <code className="font-mono text-zinc-200">{origin}</code> com o seu e-mail e senha de admin daqui.</li>
          <li>Escolha o banco e o snapshot e clique em <b>Puxar e restaurar</b>. As chaves de API vão junto — os clientes só precisam trocar a URL.</li>
        </ol>
        <details className="text-sm text-zinc-400">
          <summary className="cursor-pointer text-zinc-300">Prefere terminal? (curl)</summary>
          <Code testId="migrate-curl">{`# no servidor de DESTINO (token = login admin de lá):
curl -X POST http://127.0.0.1:8100/api/backup/pull -H "Authorization: Bearer $TOKEN" -H 'Content-Type: application/json' \\
  -d '{"source_url":"${origin}","email":"ADMIN_DAQUI","password":"SENHA_DAQUI","db":"NOME_DO_BANCO","snapshot_id":"ID_DO_SNAPSHOT","restore":true}'
# acompanhar: GET /api/backup/jobs   |   baixar um .tar direto: GET ${origin}/api/databases/{db}/snapshots/{id}/export`}</Code>
        </details>
      </div>

      <AlertDialog open={!!orphanRestore} onOpenChange={(o) => !o && setOrphanRestore(null)}>
        <AlertDialogContent className="bg-zinc-900 border-zinc-800 rounded-sm" data-testid="orphan-restore-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle className="font-display">Restaurar snapshot recebido</AlertDialogTitle>
            <AlertDialogDescription>O banco <b>{orphanRestore?.db}</b> será criado neste servidor a partir do snapshot {fmtSnapId(orphanRestore?.id)}. Informe outro nome se preferir.</AlertDialogDescription>
          </AlertDialogHeader>
          <div className="space-y-1.5">
            <Label htmlFor="orphan-as" className="text-zinc-300">Nome do banco (opcional)</Label>
            <Input id="orphan-as" value={orphanAs} onChange={(e) => setOrphanAs(e.target.value)} placeholder={orphanRestore?.db} data-testid="orphan-restore-as-input" className="bg-zinc-950 border-zinc-800 rounded-sm font-mono" />
          </div>
          <AlertDialogFooter>
            <AlertDialogCancel className="rounded-sm border-zinc-700">Cancelar</AlertDialogCancel>
            <AlertDialogAction onClick={doOrphanRestore} data-testid="orphan-restore-confirm" className="rounded-sm bg-orange-600 hover:bg-orange-500 text-white btn-tactile">Restaurar</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
      <ConfirmDialog open={!!orphanDel} onOpenChange={(o) => !o && setOrphanDel(null)} title="Apagar snapshot recebido?" description={`${orphanDel?.db} · ${fmtSnapId(orphanDel?.id)} será removido do disco deste servidor.`} onConfirm={doOrphanDelete} testId="orphan-delete-dialog" />
    </div>
  );
}
